import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Skeleton } from './ui/skeleton';
import { Megaphone, Flag, ArrowDownCircle, ArrowUpCircle, Calendar, Coins } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `R ${Math.abs(Number(v) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d).slice(0, 10)
    : dt.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
};

const KIND = {
  milestone: { icon: Flag, dot: 'bg-slate-500', label: 'text-slate-700' },
  update: { icon: Megaphone, dot: 'bg-[#1D2D2A]', label: 'text-[#1D2D2A]' },
  your_investment: { icon: ArrowUpCircle, dot: 'bg-emerald-600', label: 'text-emerald-700' },
  your_distribution: { icon: ArrowDownCircle, dot: 'bg-blue-600', label: 'text-blue-700' },
  debt_settlement: { icon: Coins, dot: 'bg-amber-500', label: 'text-amber-700' },
};

/**
 * Investor-facing deal detail: summary, your position, and a chronological
 * timeline of the fund's posted updates plus key milestones and your own
 * investments / payouts (backend: GET /investor/deal-room/deals/{id}).
 */
export function DealRoomDealDetail({ dealId, token, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dealId) return;
    setData(null);
    setError(null);
    axios.get(`${API_URL}/investor/deal-room/deals/${dealId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not load this deal'));
  }, [dealId, token]);

  const d = data?.deal;
  const pos = data?.your_position;

  return (
    <Dialog open={!!dealId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="flex flex-col gap-3 p-4 w-screen max-w-none h-[100dvh] max-h-[100dvh] rounded-none sm:p-6 sm:w-full sm:max-w-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-lg"
        data-testid="deal-detail-dialog"
      >
        <DialogHeader className="text-left pr-8">
          <DialogTitle className="text-xl">{d?.name || 'Deal'}</DialogTitle>
          <DialogDescription>{d?.counterparty || ' '}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-5 -mx-1 px-1">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!data && !error && (
            <div className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-40 w-full" /></div>
          )}

          {d && (
            <>
              {d.description && <p className="text-sm text-[#6C757D] whitespace-pre-line">{d.description}</p>}

              {pos?.has_exposure && (
                <div className="grid grid-cols-2 gap-3" data-testid="deal-detail-position">
                  <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3">
                    <p className="text-xs text-emerald-800">Your investment</p>
                    <p className="text-lg font-semibold text-emerald-900">{fmt(pos.invested)}</p>
                  </div>
                  <div className="rounded-lg border bg-blue-50 border-blue-200 p-3">
                    <p className="text-xs text-blue-800">Returned to you so far</p>
                    <p className="text-lg font-semibold text-blue-900">{fmt(pos.returned_to_you)}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-[#6C757D]">Return</p>
                  <p className="font-semibold">
                    {d.status === 'concluded' && d.actual_return_pct != null
                      ? `${Number(d.actual_return_pct).toFixed(2)}% actual`
                      : (d.indicative_return_low != null
                        ? `${d.indicative_return_low}–${d.indicative_return_high}% indicative`
                        : (d.indicative_return_note || 'TBC'))}
                  </p>
                </div>
                {d.expected_settlement_date && (
                  <div>
                    <p className="text-xs text-[#6C757D] flex items-center gap-1"><Calendar className="h-3 w-3" /> Expected settlement</p>
                    <p className="font-semibold">{fmtDate(d.expected_settlement_date)}</p>
                  </div>
                )}
              </div>
              {data.debt_leg && (
                <div className="rounded-lg border p-3 space-y-2" data-testid="deal-detail-debt-leg">
                  <div className="flex flex-wrap justify-between gap-2 text-sm">
                    <span className="font-medium">Total deal value {fmt(d.total_deal_value)}</span>
                    <span className="text-xs text-[#6C757D]">Debt {fmt(data.debt_leg.drawn)} · Deal Room {fmt(d.equity_value)}</span>
                  </div>
                  <p className="text-xs text-[#6C757D]">
                    The debt portion is repaid first; the Deal Room portion is settled after it.
                  </p>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Debt portion settled</span>
                      <span>{fmt(data.debt_leg.repaid)} · {data.debt_leg.settled_pct}%</span>
                    </div>
                    <Progress value={Math.min(data.debt_leg.settled_pct, 100)} className="h-2" />
                    <p className="text-xs text-[#6C757D] mt-1">
                      {data.debt_leg.fully_settled
                        ? `Debt portion fully settled on ${fmtDate(data.debt_leg.settled_on)}.`
                        : `${fmt(data.debt_leg.outstanding)} still to be settled on the debt portion.`}
                    </p>
                  </div>
                </div>
              )}
              {d.collection_progress_pct != null && (
                <div>
                  <p className="text-xs text-[#6C757D] flex items-center gap-1 mb-1"><Coins className="h-3 w-3" /> Collected so far</p>
                  <Progress value={Math.min(d.collection_progress_pct, 100)} className="h-2" />
                  <p className="text-xs text-[#6C757D] mt-1">
                    {d.collections ? `${fmt(d.collections.collected_total)} of ${fmt(d.collections.expected_total)} · ` : ''}{d.collection_progress_pct}% of the expected amount
                  </p>
                  {d.collections?.expected_debt > 0 && (
                    <p className="text-xs text-[#6C757D]">
                      Debt portion {fmt(d.collections.collected_debt)} of {fmt(d.collections.expected_debt)} · Deal Room portion {fmt(d.collections.collected_manual)}{d.collections.expected_manual != null ? ` of ${fmt(d.collections.expected_manual)}` : ''}
                    </p>
                  )}
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  Timeline
                  <Badge variant="outline" className="text-xs font-normal">{data.update_count} update(s)</Badge>
                </h3>
                {data.timeline.length === 0 ? (
                  <p className="text-sm text-[#6C757D]">No updates have been posted yet.</p>
                ) : (
                  <ol className="relative border-l border-slate-200 ml-2" data-testid="deal-detail-timeline">
                    {data.timeline.map((e, i) => {
                      const k = KIND[e.kind] || KIND.update;
                      const Icon = k.icon;
                      return (
                        <li key={i} className="ml-5 pb-5 last:pb-0">
                          <span className={`absolute -left-[7px] mt-1.5 h-3.5 w-3.5 rounded-full ring-4 ring-white ${k.dot}`} />
                          <p className="text-xs text-[#6C757D]">{fmtDate(e.at)}</p>
                          <p className={`text-sm font-medium flex items-center gap-1.5 ${k.label}`}>
                            <Icon className="h-3.5 w-3.5" /> {e.title}
                            {e.amount != null && <span className="font-semibold">· {fmt(e.amount)}</span>}
                            {e.kind === 'your_distribution' && !e.settled && (
                              <Badge variant="outline" className="text-[10px] ml-1">pending</Badge>
                            )}
                          </p>
                          {e.message && <p className="text-sm text-[#212529] mt-1 whitespace-pre-line">{e.message}</p>}
                          {e.detail && <p className="text-sm text-[#212529] mt-1">{e.detail}</p>}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DealRoomDealDetail;
